import { gql } from '@apollo/client';

// ============ STRUCTURE FRAGMENTS ============

export const SH_STRUCTURE_BASE_FRAGMENT = gql`
  fragment ShStructureBaseFields on _SH_Structure {
    id
    name
    address
    city
    cap
    phone
    email
    description
    province
    placeId
    lat
    lng
    openingTime
    closingTime
    clientTags
    expenseTags
  }
`;

export const SH_STRUCTURE_MEDIA_FRAGMENT = gql`
  fragment ShStructureMediaFields on _SH_Structure {
    media {
      immagine_primaria
      img_secon
      logo
      gallery
      images {
        id
        filename
        url
        tags
        label
        description
        order
      }
      views {
        id
        name
        description
        tags
        layout
        order
      }
    }
  }
`;

export const SH_STRUCTURE_COLLABORATORS_FRAGMENT = gql`
  fragment ShStructureCollaboratorsFields on _SH_Structure {
    collaborators {
      id
      name
      email
      phone
      role
      notes
      sportTypes
      modulesEnabledToAccess
      permissionsEnabledToAccess
      createdAt
      updatedAt
    }
  }
`;

export const SH_STRUCTURE_INSTRUCTORS_FRAGMENT = gql`
  fragment ShStructureInstructorsFields on _SH_Structure {
    instructors {
      id
      firstName
      lastName
      email
      phone
      metadata
      disponibilita {
        weeklyMatches
        days
        slots
        bySport
      }
      createdAt
      updatedAt
    }
  }
`;

export const SH_STRUCTURE_FRAGMENT = gql`
  fragment ShStructureFields on _SH_Structure {
    ...ShStructureBaseFields
    ...ShStructureMediaFields
    ...ShStructureCollaboratorsFields
    ...ShStructureInstructorsFields
  }
  ${SH_STRUCTURE_BASE_FRAGMENT}
  ${SH_STRUCTURE_MEDIA_FRAGMENT}
  ${SH_STRUCTURE_COLLABORATORS_FRAGMENT}
  ${SH_STRUCTURE_INSTRUCTORS_FRAGMENT}
`;

// ============ FIELD FRAGMENTS ============

export const SH_FIELD_FRAGMENT = gql`
  fragment ShFieldFields on _SH_Field {
    sportTypes
    capabilities
    id
    name
    structureId
    resourceType
    sportType
    allowedReservationTypes
    type
    description
    slotDuration
    timeSlotLession
    slotStrategy
    slotStep
    maxPlayers
    playersPerTeam
    openingTime
    closingTime
    matchPrice
  }
`;

// ============ PLAYER FRAGMENTS ============

export const SH_PLAYER_FRAGMENT = gql`
  fragment ShPlayerFields on _SH_Player {
    id
    firstName
    lastName
    profileImageUrl
    email
    phone
    notes
    tesserato
    tags
    punti
    livello
    disponibilita {
      weeklyMatches
      days
      slots
      bySport
    }
    status
    memberships {
      structureId
      joinedAt
    }
    fieldType {
      fieldTypeId
      joinedAt
    }
    createdAt
    updatedAt
  }
`;

// ============ RESERVATION FRAGMENTS ============

export const SH_RESERVATION_BASE_FRAGMENT = gql`
  fragment ShReservationBaseFields on Reservation {
    id
    parentModuleId
    parentResourceId
    sportType
    title
    status
    startAt
    endAt
    notes
    email
    phone
    metadata
    reservationType
    isLession
    lessonPlayersCount
    instructorId
    paid
    recurrence {
      id
      originReservationId
      originMatchId
      parentResourceId
      reservationType
      status
      summary
      nextReservationId
      nextOccurrenceKey
      nextOccurrenceStartAt
      nextOccurrenceEndAt
      lastAttemptAt
      lastConflictAt
      lastCreatedAt
    }
    createdAt
    updatedAt
  }
`;

export const SH_RESERVATION_MATCH_SUMMARY_FRAGMENT = gql`
  fragment ShReservationMatchSummaryFields on Reservation {
    matchSummary {
      maxPlayers
      players {
        team
        position
        name
        paid
        paidAmount
        unpaidAmount
        paymentMethod
      }
    }
  }
`;

export const SH_RESERVATION_FRAGMENT = gql`
  fragment ShReservationFields on Reservation {
    ...ShReservationBaseFields
    ...ShReservationMatchSummaryFields
  }
  ${SH_RESERVATION_BASE_FRAGMENT}
  ${SH_RESERVATION_MATCH_SUMMARY_FRAGMENT}
`;

// ============ MATCH FRAGMENTS ============

export const SH_MATCH_PLAYER_FRAGMENT = gql`
  fragment ShMatchPlayerFields on _SH_MatchPlayer {
    id
    matchId
    team
    position
    name
    userId
    profileImageUrl
    role
    notes
    metadata
    paid
    paidAmount
    shopProducts
    createdAt
    updatedAt
  }
`;

export const SH_MATCH_SNAPSHOTS_FRAGMENT = gql`
  fragment ShMatchSnapshotsFields on _SH_Match {
    fieldSnapshot {
      # Resource IDs are not snapshot identities; keep historical values embedded in each match.
      __typename @skip(if: true)
      id
      name
      slotDuration
      type
      maxPlayers
      playersPerTeam
      matchPrice
    }
    structureSnapshot {
      __typename @skip(if: true)
      id
      name
      address
      city
      cap
    }
  }
`;

export const SH_MATCH_FRAGMENT = gql`
  fragment ShMatchFields on _SH_Match {
    id
    clientId
    reservationId
    token
    status
    title
    sportType
    startAt
    endAt
    durationMinutes
    organizer {
      playerId
      name
      firstName
      lastName
      email
      phone
    }
    ...ShMatchSnapshotsFields
    notes
    metadata
    result
    players {
      ...ShMatchPlayerFields
    }
    createdAt
    updatedAt
  }
  ${SH_MATCH_PLAYER_FRAGMENT}
  ${SH_MATCH_SNAPSHOTS_FRAGMENT}
`;

// ============ ERROR FRAGMENT ============

export const ERROR_FRAGMENT = gql`
  fragment ErrorFields on Error {
    path
    message
  }
`;
